import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import Skeleton from '@material-ui/lab/Skeleton';
import Link from '@material-ui/core/Link';
import T from 'i18n-react';
import Typography from '@material-ui/core/Typography';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import clsx from 'clsx';
import { getFullAddress } from './utils';
import Logo from './Logo';
import Map from './Map';
import { createBusinessPageShareText } from '../Social/TwitterButton';
import { FRONT_BUSINESS_URL } from '../routing';
import SnsButtonGroup from './SnsButtonGroup';

const MapWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing(3, 0)};
`;

export const styles = (theme) => ({
  root: {
    position: 'relative',
  },
  infoTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
      marginBottom: theme.spacing(0),
    },
  },
  xsName: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  smName: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  name: {
    display: 'block',
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(18),
    margin: theme.spacing(2, 0),
  },
  openIcon: {
    fontSize: theme.typography.pxToRem(18),
    marginLeft: theme.spacing(1),
    verticalAlign: 'text-top',
  },
  address: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  addressInfo: {
    position: 'relative',
  },
  homepage: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    textDecoration: 'underline',
    fontSize: theme.typography.pxToRem(14),
  },
});

class Info extends React.Component {
  getName() {
    const { name, displayName } = this.props;
    return displayName || name;
  }

  mainContentRenderer() {
    const {
      classes,
      id,
      loaded,
      logo,
      url,
      address,
      canShowShareButton,
      pageUrl: pageUrlFromProps,
    } = this.props;
    const name = this.getName();
    const businessPageUrl =
      pageUrlFromProps ?? FRONT_BUSINESS_URL.replace(':id', id);
    const regionName = address?.region || '';
    const twitterShareText = createBusinessPageShareText(
      name,
      businessPageUrl,
      regionName,
    );

    return (
      <>
        {canShowShareButton && (
          <SnsButtonGroup
            hiddenProps={{ only: 'xs' }}
            loaded={loaded}
            gutterBottom
            tweetText={twitterShareText}
            url={businessPageUrl}
          />
        )}
        <div>
          <Logo src={logo} alt={name} loaded={loaded} />
        </div>
        <div className={classes.xsName}>{this.nameRenderer(name, url)}</div>
        <div className={classes.smName}>{this.nameRenderer(name, url)}</div>
      </>
    );
  }

  otherRenderer() {}

  addressDialog() {}

  titleRenderer() {
    const { classes } = this.props;

    return (
      <Typography className={classes.infoTitle} align="center">
        {T.translate('business.infoTitle')}
      </Typography>
    );
  }

  nameRenderer(name, url) {
    const { classes, loaded } = this.props;
    if (!loaded)
      return <Skeleton width={150} height={30} className={classes.name} />;

    return url ? (
      <Link
        href={url}
        underline="always"
        target="_blank"
        className={clsx('text-break-all', classes.name)}
      >
        {name}
        <OpenInNewIcon className={classes.openIcon} />
      </Link>
    ) : (
      <span className={clsx('text-break-all', classes.name)}>{name}</span>
    );
  }

  homepageRenderer(homepage) {
    return (
      <div>
        <Link
          href={homepage}
          underline="always"
          target="_blank"
          rel="noopener"
          className={clsx('text-break-all', this.props.classes.homepage)}
        >
          {homepage}
        </Link>
      </div>
    );
  }

  render() {
    const { classes, loaded, homepage, address = {}, theme } = this.props;
    const { zip } = address;
    const addressText = getFullAddress(address);

    return (
      <ThemeProvider theme={theme}>
        <div className={classes.root}>
          {this.titleRenderer()}
          {this.mainContentRenderer()}
          {loaded ? (
            <div className={classes.addressInfo}>
              {!!zip && (
                <Typography className={classes.address}>{`${T.translate(
                  'zipMark',
                )}${zip}`}</Typography>
              )}
              <Typography className={clsx('text-break-all', classes.address)}>
                {addressText}
              </Typography>
              {this.addressDialog()}
            </div>
          ) : (
            <Skeleton width={150} height={50} />
          )}
          {!!homepage && this.homepageRenderer(homepage)}
          {addressText !== '' && (
            <MapWrapper>
              <Map addressText={addressText} />
            </MapWrapper>
          )}
          {this.otherRenderer()}
        </div>
      </ThemeProvider>
    );
  }
}

Info.propTypes = {
  loaded: PropTypes.bool,
  id: PropTypes.number,
  name: PropTypes.string,
  displayName: PropTypes.string,
  homepage: PropTypes.string,
  logo: PropTypes.string,
  url: PropTypes.string,
  address: PropTypes.shape({
    region: PropTypes.string,
    regionId: PropTypes.number,
    city: PropTypes.string,
    cityKana: PropTypes.string,
    streetAddress: PropTypes.string,
    building: PropTypes.string,
    zip: PropTypes.string,
    airport: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      code: PropTypes.string,
      region: PropTypes.string,
      regionId: PropTypes.number,
    }),
  }),
  attractions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      imageUrl: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
};

export default Info;
